_collow.components.Courses = class Courses extends _collow.components.Base {
  root;
  elements;
  params;
  originalCourses;
  allowedCourses;
  courseGroups;
  userIsLogged;
  urlParams;
  // Create reusable constants
  static COLLOW_PROJECT = "Collow";
  static FREE_COURSE_CODE = 116;

  // Static data cache to prevent redundant API calls
  static courseCache = {
    allCourses: null,
    allowedCourses: null,
    timestamp: null,
    isLoading: false,
    callbacks: [],
  };

  constructor(root, elements, params) {
    super();

    this.root = root;
    this.elements = elements;
    this.params = params;
    this.urlParams = new URLSearchParams(window.location.search);

    // Use a single event listener and delegate based on element type
    const filterElements = [elements.studypoints, elements.difficulty, elements.certificate, elements.tags];
    for (const element of filterElements) {
      this.listen(element, 'change', this.filters);
    }

    this.userIsLogged = window.localStorage.getItem('token');

    this.setFollowedCourses();
    // Use this.originalCourses = Array.from() instead of spread operator for better performance
    this.originalCourses = Array.from(this.elements.courseContainer.childNodes);

    // Listen for whitelabel features loaded event
    window.addEventListener("_collow.whitelabel-features-loaded", () => {
      // Initialize course list only after whitelabel features are loaded
      this.initCoursesList();
    });
    
    // Use single listener for user events
    window.addEventListener("_collow.current-user", this.handleUserChange);
    
    // Check if whitelabel features are already loaded
    const whitelabelFeatures = window.localStorage.getItem('whitelabelFeatures');
    if (whitelabelFeatures) {
      // If features are already loaded, initialize courses immediately
      this.initCoursesList();
    }
  }

  handleUserChange = () => {
    // Clear the cache when user changes to force refresh of courses data
    Courses.courseCache.allowedCourses = null;
    Courses.courseCache.timestamp = null;
    this.initCoursesList();
  };

  /**  check if course is being followed and change button accordingly */
  setFollowedCourses = () => {
    const courses = localStorage.getCourses();
    if (!courses || !courses.length) return;

    // Use a DocumentFragment for batch DOM updates
    const courseNodes = this.elements.courseContainer.childNodes;
    for (let i = 0; i < courseNodes.length; i++) {
      const el = courseNodes[i];
      if (!el || !el.dataset || !el.dataset.link) continue;

      const link = el.dataset.link;
      // Use faster array find with early exit
      let nextlink = null;
      for (let j = 0; j < courses.length; j++) {
        const item = courses[j];
        if (item && item.course && item.course["unique_identifer"] === link) {
          nextlink = item;
          break;
        }
      }

      if (nextlink) {
        const button = el.querySelector("[data-card-button]");
        if (button && button.dataset) {
          button.textContent = button.dataset.continueText;
        }
      }
    }

    this.originalCourses = Array.from(this.elements.courseContainer.childNodes);
  };

  /** Sets courses as greyed out if they are not free and not allowed for the current user. */
  setGreyCourses = () => {
    if (!Array.isArray(this.allowedCourses)) return;

    const courseNodes = this.elements.courseContainer.childNodes;
    const strippedLinks = new Set();
    if (this.allowedCourses) {
      for (let i = 0; i < this.allowedCourses.length; i++) {
        strippedLinks.add(this.allowedCourses[i].toLowerDashed());
      }
    }

    for (let i = 0; i < courseNodes.length; i++) {
      const el = courseNodes[i];
      if (!el || !el.dataset) continue;

      const link = el.dataset.link;
      if (!link) continue;

      if (!strippedLinks.has(link)) {
        el.classList.add("grey-status");
        const button = el.querySelector("[data-card-button]");
        if (button && button.dataset) {
          button.textContent = button.dataset.checkoutText;
        }
      }
    }
  };

  /** Populates the dropdown menu with unique tags extracted from the original courses. */
  addTagsToDropdown = () => {
    if (!Array.isArray(this.originalCourses) || !this.originalCourses.length) return;

    // Use Set for unique tags from the beginning
    const tags = new Set();

    // Optimize tag extraction - avoid creating intermediate arrays
    for (let i = 0; i < this.originalCourses.length; i++) {
      const course = this.originalCourses[i];
      if (course && course.dataset && course.dataset.tags && !course.dataset.tags.includes("empty")) {
        const courseTags = course.dataset.tags.split(',');
        for (const tag of courseTags) {
          if (tag) tags.add(tag);
        }
      }
    }

    // Use DocumentFragment for batch DOM updates
    const fragment = document.createDocumentFragment();
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = 'Tags';
    fragment.appendChild(emptyOption);

    // Convert Set to sorted array once
    const sortedTags = Array.from(tags).sort();

    // Add each unique tag to the dropdown
    for (const tag of sortedTags) {
      const optionElement = document.createElement('option');
      if (tag.includes(",")) {
        const subTags = tag.split(",");
        for (const subTag of subTags) {
          if (subTag) {
            optionElement.value = subTag;
            optionElement.textContent = subTag.replace("-", " ");
          }
        }
      } else {
        optionElement.value = tag;
        optionElement.textContent = tag.replace("-", " ");
      }
      fragment.appendChild(optionElement);
    }

    // Clear and append in one batch
    this.elements.tags.innerHTML = '';
    this.elements.tags.appendChild(fragment);
    this.elements.tags.removeAttribute("hidden");
  }

  /** Applies filtering to the courses based on the selected criteria from input elements. */
  filters = () => {
    const { difficulty, studypoints, certificate, tags } = this.elements;
    let startingElements = this.originalCourses;

    // Combine all filters into a single pass through the data
    if (difficulty.value) {
      startingElements = this.sortByDifficulty(difficulty.value, startingElements);
      this.updateQueryParams("difficulty", difficulty.value);
    } else {
      this.updateQueryParams("difficulty", null);
    }

    if (studypoints.value) {
      startingElements = this.sortByPoints(studypoints.value, startingElements);
      this.updateQueryParams("studypoints", studypoints.value);
    } else {
      this.updateQueryParams("studypoints", null);
    }

    if (certificate.value) {
      startingElements = this.sortByCertificate(certificate.value, startingElements);
      this.updateQueryParams("certificate", certificate.value);
    } else {
      this.updateQueryParams("certificate", null);
    }

    if (tags.value) {
      startingElements = this.sortByTag(tags.value, startingElements);
      this.updateQueryParams("tags", tags.value);
    } else {
      this.updateQueryParams("tags", null);
    }

    // Add the filtered courses to the page body
    this.addCoursesToBody(startingElements);
  };

  /** Sorts an array of courses based on their points value. */
  sortByPoints = (points, courses) => {
    if (!Array.isArray(courses)) return [];

    const sort = points === "highFirst" ? 1 : -1;
    // Use filter + sort in a single operation for better performance
    return courses.filter(course => course && course.dataset && course.dataset.points)
      .sort((a, b) => {
        const aPoints = Number(a.dataset.points);
        const bPoints = Number(b.dataset.points);
        return aPoints < bPoints ? sort : -sort;
      });
  };

  /** Filters an array of courses to include only those that match the specified difficulty level. */
  sortByDifficulty = (difficulty, courses) => {
    if (!Array.isArray(courses)) return [];

    // Use quick filter with explicit return
    return courses.filter(course =>
      course && course.dataset && course.dataset.difficulty === difficulty
    );
  };

  /** Filters an array of courses to include only those that match the specified certificate type. */
  sortByCertificate = (certificate, courses) => {
    if (!Array.isArray(courses)) return [];

    // Use quick filter with explicit return
    return courses.filter(course =>
      course && course.dataset && course.dataset.certificate === certificate
    );
  };

  /** Filters an array of courses to include only those that have the specified tag. */
  sortByTag = (tag, courses) => {
    if (!Array.isArray(courses)) return [];

    // Use quick filter with explicit return
    return courses.filter(course =>
      course && course.dataset && course.dataset.tags && course.dataset.tags.includes(tag)
    );
  };

  /** Fetches allowed courses for current user and populates the list. */
  initCoursesList = async () => {
    // Ensure whitelabel features are available
    const whitelabelFeatures = window.localStorage.getItem('whitelabelFeatures');
    if (!whitelabelFeatures) {
      console.log('Waiting for whitelabel features to be loaded');
      return; // Exit if whitelabel features aren't loaded yet
    }
    
    const accesibleFeatures = JSON.parse(whitelabelFeatures || '{}');
    const hasUserGroups = accesibleFeatures?.has_user_groups || false;

    try {
      // Fetch allowed courses and all courses in parallel, using cache when available
      if (this.userIsLogged && hasUserGroups) {
        await this.fetchAllowedCourses();
      }

      await this.fetchAllCourses();

      this.addCoursesToBody();
      this.addTagsToDropdown();
      this.initImages();

      if (this.urlParams.toString()) {
        this.getQueryParamsAsObjects();
      }
    } catch (err) {
      console.error('Error initializing courses list:', err);
      this.originalCourses = [];
    }
  }

  /** Filters the list of courses based on whether the user is logged in and whether the course is private. */
  hidePrivateCourses = (courses) => {
    if (!Array.isArray(courses)) return [];

    if (this.userIsLogged) {
      // For logged-in users with allowedCourses
      if (Array.isArray(this.allowedCourses)) {
        return courses.filter(course => {
          if (!course || !course.dataset) return false;
          // Public courses or private courses the user has access to
          return course.dataset.private !== "true" ||
                (course.dataset.private === "true" && this.allowedCourses.includes(course.dataset.link));
        });
      }
      // For logged-in users without allowedCourses, show all public courses
      return courses.filter(course =>
        course && course.dataset && course.dataset.private !== "true"
      );
    }

    // For non-logged in users, only show public courses
    return courses.filter(course =>
      course && course.dataset && course.dataset.private === "false"
    );
  }

  /** Adds a 'groups' attribute to each course element based on the course's group data.*/
  addCourseDataGroupAttr = (courses) => {
    if (!Array.isArray(courses) || !Array.isArray(this.courseGroups)) return courses;

    // Create a map for faster lookups
    const courseGroupMap = {};
    for (const item of this.courseGroups) {
      if (item && item.courseName) {
        courseGroupMap[item.courseName] = item.groups || '';
      }
    }

    for (const course of courses) {
      if (!course || !course.dataset) continue;

      const courseName = course.dataset.link;
      if (!courseName) continue;

      course.dataset.groups = courseGroupMap[courseName] || '';
    }

    return courses;
  }

  /** Adds a 'usersReg' attribute to each course element based on the course's usersReg data.*/
  addCourseDataUsersRegisteredAttr = (courses) => {
    if (!Array.isArray(courses) || !Array.isArray(this.courseGroups)) return courses;

    // Create a map for faster lookups
    const courseUserMap = {};
    for (const item of this.courseGroups) {
      if (item && item.courseName) {
        courseUserMap[item.courseName] = item.registeredUsers || 0;
      }
    }

    for (const course of courses) {
      if (!course || !course.dataset) continue;

      const courseName = course.dataset.link;
      if (!courseName) continue;

      course.dataset.usersReg = courseUserMap[courseName] || 0;
    }

    return courses;
  }

  /** Updates the course element with the number of registered users. */
  addRegisteredUsersInfo = (course) => {
    if (!course || !course.dataset || !course.getAttribute('data-participantsCount')) return course;

    const isCollow = this.params.project === Courses.COLLOW_PROJECT;

    if (isCollow && course.dataset.usersReg && parseInt(course.dataset.usersReg) >= 3) {
      const participantsCount = course.getAttribute('data-participantsCount').replaceAll("_", " ");
      const regUsersParagraph = course.querySelector(".users-registered");
      if (regUsersParagraph) {
        regUsersParagraph.textContent = `${course.dataset.usersReg}+ ${participantsCount}`;
      }
    }
    return course;
  }

  /** Sorts courses by their payment status for the current user. */
  sortCoursesByPaymentStatus = (courses) => {
    if (!Array.isArray(courses)) return [];

    // Apply data attributes to courses
    const coursesWithData = this.addCourseDataUsersRegisteredAttr(
      this.addCourseDataGroupAttr(courses)
    );

    if (!coursesWithData.length) return [];

    const userIsLogged = Array.isArray(this.allowedCourses);
    const freeCourseCod = Courses.FREE_COURSE_CODE;

    // Create course maps for faster processing
    const allCoursesMap = new Map();
    for (const course of coursesWithData) {
      if (course && course.dataset && course.dataset.link) {
        allCoursesMap.set(course.dataset.link, course);
      }
    }

    // Get free courses - using direct filtering for performance
    const freeCourses = coursesWithData.filter(el =>
      el && el.dataset && el.dataset.groups &&
      String(el.dataset.groups).includes(freeCourseCod)
    );

    // Create sets for faster lookups
    const freeCoursesSet = new Set();
    for (const course of freeCourses) {
      if (course && course.dataset && course.dataset.link) {
        freeCoursesSet.add(course.dataset.link);
      }
    }

    // Handle purchased and unpurchased courses based on login status
    let purchasedCourses = [];
    let unpurchasedCourses = [];

    if (userIsLogged) {
      // For logged-in users
      const allowedCoursesSet = new Set(this.allowedCourses);

      // Get purchased courses that aren't free
      purchasedCourses = coursesWithData.filter(course =>
        course && course.dataset && course.dataset.link &&
        allowedCoursesSet.has(course.dataset.link) &&
        !freeCoursesSet.has(course.dataset.link)
      );

      // Get unpurchased courses
      unpurchasedCourses = coursesWithData.filter(course =>
        course && course.dataset && course.dataset.link &&
        !allowedCoursesSet.has(course.dataset.link) &&
        !freeCoursesSet.has(course.dataset.link)
      );
    } else {
      // For guests, all non-free courses are unpurchased
      unpurchasedCourses = coursesWithData.filter(course =>
        course && course.dataset && course.dataset.link &&
        !freeCoursesSet.has(course.dataset.link)
      );
    }

    // Combine all categories
    return [...freeCourses, ...purchasedCourses, ...unpurchasedCourses]
      .filter(Boolean);
  }

  /** Populates the list with either courses or with a message, in case no courses are available. */
  addCoursesToBody = (courses = this.originalCourses) => {
    if (!courses) return;

    // Use DOM fragment for better performance
    const fragment = document.createDocumentFragment();
    const isCollow = this.params.project === Courses.COLLOW_PROJECT;

    if (courses && courses.length) {
      this.elements.noResults.setAttribute('hidden', true);
      this.elements.courseContainer.removeAttribute('hidden');

      let coursesToRender = courses;

      if (isCollow) {
        const newCourses = this.hidePrivateCourses(courses);
        coursesToRender = this.sortCoursesByPaymentStatus(newCourses);
      }

      // Filter and add courses to fragment
      for (const course of coursesToRender) {
        if (!course) continue;
        const newCourse = this.addRegisteredUsersInfo(course);
        if (newCourse) {
          fragment.appendChild(newCourse.cloneNode(true));
        }
      }

      // Clear and update DOM in one operation
      this.elements.courseContainer.innerHTML = '';
      this.elements.courseContainer.appendChild(fragment);

      // Reinitialize images after updating the DOM
      this.initImages();
    } else {
      this.elements.courseContainer.setAttribute('hidden', true);
      this.elements.noResults.removeAttribute('hidden');
    }
  }

  /** Initializes each image class manually. */
  initImages = () => {
    // Use direct selector for better performance
    const imageElements = this.elements.courseContainer.querySelectorAll('.course-card__img-background');
    for (const imageElement of imageElements) {
      _collow.components.Image.fromElement(imageElement);
    }
  }

  /** Fetches all courses */
  fetchAllCourses = async () => {
    // Check cache first
    if (Courses.courseCache.allCourses) {
      this.courseGroups = Courses.courseCache.allCourses;
      return;
    }

    // If another component is already loading the data, wait for it to complete
    if (Courses.courseCache.isLoading) {
      return new Promise((resolve) => {
        Courses.courseCache.callbacks.push(() => {
          this.courseGroups = Courses.courseCache.allCourses;
          resolve();
        });
      });
    }

    // Set loading flag
    Courses.courseCache.isLoading = true;

    try {
      const res = await fetching(this.params.path + "/course");
      if (Array.isArray(res)) {
        const courseGroups = res.map(course => ({
          courseName: course.unique_identifer,
          groups: course.exclusive_groups || [],
          registeredUsers: course.registered_users || 0
        }));

        // Update the cache
        Courses.courseCache.allCourses = courseGroups;
        Courses.courseCache.timestamp = Date.now();
        this.courseGroups = courseGroups;

        // Execute any pending callbacks
        Courses.courseCache.callbacks.forEach(callback => callback());
        Courses.courseCache.callbacks = [];
      }
    } catch (error) {
      console.error("Error fetching all courses:", error);
      this.courseGroups = [];
    } finally {
      Courses.courseCache.isLoading = false;
    }
  }

  /** Fetches allowed courses for current user/user group */
  fetchAllowedCourses = async () => {
    // Skip if not logged in
    if (!this.userIsLogged) {
      this.allowedCourses = [];
      return;
    }

    // Check cache first - use cache if it exists and is less than 5 minutes old
    const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    if (Courses.courseCache.allowedCourses &&
        Courses.courseCache.timestamp &&
        now - Courses.courseCache.timestamp < CACHE_TIMEOUT) {
      this.allowedCourses = Courses.courseCache.allowedCourses;

      // Apply grey courses if needed
      if (this.params.project === Courses.COLLOW_PROJECT) {
        this.setGreyCourses();
      } else {
        this.filterOriginalCourses();
      }
      return;
    }

    // If another component is already loading the data, wait for it to complete
    if (Courses.courseCache.isLoading) {
      return new Promise((resolve) => {
        Courses.courseCache.callbacks.push(() => {
          this.allowedCourses = Courses.courseCache.allowedCourses;
          if (this.params.project === Courses.COLLOW_PROJECT) {
            this.setGreyCourses();
          } else {
            this.filterOriginalCourses();
          }
          resolve();
        });
      });
    }

    // Set loading flag
    Courses.courseCache.isLoading = true;

    try {
      const res = await fetching(this.params.path + "/course/allowed_courses");

      // Ensure res is an array before mapping
      const allowedCourses = Array.isArray(res) ? res.map(course => course.unique_identifer) : [];

      // Update the cache
      Courses.courseCache.allowedCourses = allowedCourses;
      Courses.courseCache.timestamp = Date.now();
      this.allowedCourses = allowedCourses;

      if (this.params.project !== Courses.COLLOW_PROJECT) {
        this.filterOriginalCourses();
      } else {
        this.setGreyCourses();
      }

      // Execute any pending callbacks
      Courses.courseCache.callbacks.forEach(callback => callback());
      Courses.courseCache.callbacks = [];
    } catch (error) {
      console.error("Error fetching allowed courses:", error);
      this.allowedCourses = []; // Ensure allowedCourses is an empty array on error
    } finally {
      Courses.courseCache.isLoading = false;
    }
  }

  /** Filter the original courses based on allowed courses */
  filterOriginalCourses = () => {
    // Filter the originalCourses array safely
    if (Array.isArray(this.originalCourses) && this.originalCourses.length && Array.isArray(this.allowedCourses)) {
      this.originalCourses = this.originalCourses.filter(course =>
        course && course.dataset && course.dataset.link && this.allowedCourses.includes(course.dataset.link)
      );
    }
  }

  /** Processes query parameters from the URL and simulates selection for each parameter */
  getQueryParamsAsObjects = () => {
    // More efficient iteration of URL params
    for (const [key, value] of this.urlParams.entries()) {
      if (this.elements[key]) {
        this.simulateSelection(key, value);
      }
    }
  }

  /** Simulates selecting a value for a specified input element and dispatches a change event */
  simulateSelection = (selectElement, value) => {
    const selectInput = this.elements[selectElement];
    if (!selectInput) return;

    selectInput.value = value;
    // Use more efficient way to create and dispatch event
    const event = new Event('change', { bubbles: true });
    selectInput.dispatchEvent(event);
  }

  /**  Updates or removes a query parameter in the URL */
  updateQueryParams = (key, value) => {
    // Use URL object for better performance
    const url = new URL(window.location);

    if (value === null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }

    // Update history with minimal change
    window.history.pushState({}, '', url);
  }
};
